import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {
  SaveContentErrorResponse,
  SaveContentPayload,
  Locale,
  ContentResponse,
} from './faq-library/faq-library.types';
import type {AttributeMetaData} from 'src/types/writeback';
import type {Dispatch} from 'src/reducers';

import {useApiQuery} from 'src/hooks/useApiQuery';
import {useApiMutation} from 'src/hooks/useApiMutation';
import {useQueryClient} from '@tanstack/react-query';
import {
  toastApi,
  Toast,
  ToastTitle,
  ToastBody,
} from '@spaced-out/ui-design-system';
import useI18n from 'src/hooks/useI18n';
import {useNewAllowedAttributes} from 'src/hooks/product-flags';
import {getFWBKey} from './utils';
import * as reduxApi from 'src/utils/redux-api-v2';

/**
 * a hook that returns properties/accessors for working with writeback attributes
 *
 * in chatbot this value always defaults to the useNewAllowedAttributes
 * hook/flag/config value, but some parts of the codebase do not yet
 * understand this field's value, so despite it being set and available,
 * using it in journeys will cause errors. To force the use of the old
 * field key, pass in `false` and likewise to override the agency config
 * value here, pass in `true`. leaving the parameter blank will
 * @param  {[boolean]} forceOWBAttrs      an optional override for useNewAllowedAttributes
 * @return {[Object]}                     returns a simple object with {
 *                                          wbFieldIdName: string,
 *                                          getWbFieldId: (?AttributeMetaData) => string,
 *                                        }
 */
export function useOWBFields(forceOWBAttrs?: boolean): {
  wbFieldIdName: string;
  getWbFieldId: (
    arg1?: AttributeMetaData | null | undefined,
  ) => string | null | undefined;
} {
  const usesOWB = useNewAllowedAttributes();
  return getFWBKey(forceOWBAttrs != null ? forceOWBAttrs : usesOWB);
}

export function useFetchFaqLocales(brandId: string, agencyId: string) {
  const {
    error,
    data: locales,
    isLoading: isLoading,
  } = useApiQuery<Locale[]>(`chatbot/faqs/locales?brand_id=${brandId}`, {
    queryOptions: {
      enabled: Boolean(brandId) && Boolean(agencyId),
    },
  });

  return {
    error,
    locales: locales ?? [],
    isLoading,
  };
}

export function useFetchFaqContent(
  brandId: string,
  agencyId: string,
  locale: string = 'en_US',
) {
  const {
    error,
    data: content,
    isLoading,
    refetch,
  } = useApiQuery<ContentResponse>(
    `chatbot/faqs/content?brand_id=${brandId}&locale=${locale}`,
    {
      queryOptions: {
        enabled: Boolean(brandId) && Boolean(agencyId),
      },
    },
  );

  return {
    error,
    content: content ?? {text: '', default_message: ''},
    isLoading,
    refetch,
  };
}

export function useSaveFaqContent(): {
  saveFaqContent: (payload: SaveContentPayload) => Promise<unknown>;
  saveFaqContentError: SaveContentErrorResponse | null | undefined;
} {
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const {mutateAsync, error} = useApiMutation<
    ContentResponse,
    SaveContentErrorResponse,
    SaveContentPayload
  >({
    mutationFn: async ({agency_id: _agencyId, ...payload}) => {
      return dispatch(
        reduxApi.put('chatbot/faqs/content', {
          content_type: 'text',
          ...payload,
        }),
      );
    },
    onSuccess: async (_, variables) => {
      // Invalidate the content query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: [
          `chatbot/faqs/content?brand_id=${variables.brand_id}&locale=${
            variables.locale || 'en_US'
          }`,
        ],
      });

      // Invalidate the locales query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: [`chatbot/faqs/locales?brand_id=${variables.brand_id}`],
      });

      // Show success toast
      toastApi.show(
        <Toast semantic="success">
          <ToastTitle>{t('Changes_Saved', 'Changes Saved')}</ToastTitle>
          <ToastBody>
            {t(
              'Your_source_content_is_updated_You_may_now_preview_it',
              'Your source content is updated. You may now preview it',
            )}
          </ToastBody>
        </Toast>,
      );
    },
    onError: async (error) => {
      // Show error toast
      toastApi.show(
        <Toast semantic="danger">
          <ToastTitle>{t('Save_Failed', 'Save Failed')}</ToastTitle>
          <ToastBody>
            {error?.error ||
              t(
                'An_error_occurred_while_saving',
                'An error occurred while saving',
              )}
          </ToastBody>
        </Toast>,
      );
    },
  });

  return {
    saveFaqContent: mutateAsync,
    saveFaqContentError: error,
  };
}
